/**
 * SkillManager Component
 *
 * Admin component for skill management:
 * - View available skill templates (builtin, roles, custom)
 * - View agent skills (expected + installed status)
 * - Push skills to agents via SCP
 * - Verify installed skills via SSH
 *
 * @module components/admin/SkillManager
 */

import { useState, useCallback, useMemo, memo, useEffect, lazy, Suspense } from 'react';
import {
  Package,
  RefreshCw,
  Search,
  ChevronDown,
  Loader2,
  AlertCircle,
  CheckCircle,
  Bot,
  Sparkles,
  Database,
  Upload,
  Download,
  Check,
  X,
  Shield,
  Eye,
  Square,
  CheckSquare,
  FileText,
  File,
  ChevronRight,
  Clock,
  Copy,
  Plus,
  FolderPlus,
  Trash2
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Lazy load SyntaxHighlighter for code blocks
const SyntaxHighlighter = lazy(() =>
  import('react-syntax-highlighter').then(mod => ({
    default: mod.Prism
  }))
);
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

// ============================================
// Constants
// ============================================

const API_BASE = '/api';

const SKILL_CLASS_COLORS = {
  builtin: {
    bg: 'bg-blue-500/20',
    text: 'text-blue-400',
    border: 'border-blue-500/30',
    label: 'Builtin'
  },
  skills: {
    bg: 'bg-green-500/20',
    text: 'text-green-400',
    border: 'border-green-500/30',
    label: 'Skill'
  },
  imported: {
    bg: 'bg-purple-500/20',
    text: 'text-purple-400',
    border: 'border-purple-500/30',
    label: 'Imported'
  },
  unknown: {
    bg: 'bg-yellow-500/20',
    text: 'text-yellow-400',
    border: 'border-yellow-500/30',
    label: 'Agent Only'
  }
};

const VIEWS = {
  AGENT_SKILLS: 'agent_skills',
  TEMPLATES: 'templates'
};

// ============================================
// API Functions
// ============================================

async function fetchAgentSkills(agentId, verify = false) {
  const url = verify
    ? `${API_BASE}/agents/${agentId}/skills?verify=true`
    : `${API_BASE}/agents/${agentId}/skills`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch agent skills');
  return res.json();
}

async function fetchSkillTemplates() {
  const res = await fetch(`${API_BASE}/skills/templates`, {
    credentials: 'include'
  });
  if (!res.ok) throw new Error('Failed to fetch skill templates');
  return res.json();
}

async function pushSkillsToAgent(agentId, skillClass = null, skillName = null) {
  const body = {};
  if (skillClass) body.class = skillClass;
  if (skillName) body.skillName = skillName;

  const res = await fetch(`${API_BASE}/skills/push/${agentId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to push skills');
  }
  return res.json();
}

async function aggregateSkills(agentId) {
  const res = await fetch(`${API_BASE}/agents/${agentId}/skills/aggregate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include'
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to aggregate skills');
  }
  return res.json();
}

async function validateSkills(agentId) {
  const res = await fetch(`${API_BASE}/agents/${agentId}/skills/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include'
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to validate skills');
  }
  return res.json();
}

async function regenerateIdentity(agentId) {
  const res = await fetch(`${API_BASE}/agents/${agentId}/skills/identity`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include'
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to regenerate identity');
  }
  return res.json();
}

async function fetchAgents() {
  const res = await fetch(`${API_BASE}/agents?status=active`, {
    credentials: 'include'
  });
  if (!res.ok) throw new Error('Failed to fetch agents');
  return res.json();
}

async function importSkillFromAgent(agentId, skillName) {
  const res = await fetch(`${API_BASE}/skills/import/${agentId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ skillName })
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to import skill');
  }
  return res.json();
}

async function deleteSkillFromAgent(agentId, skillName) {
  const res = await fetch(`${API_BASE}/skills/push/${agentId}/${encodeURIComponent(skillName)}`, {
    method: 'DELETE',
    credentials: 'include'
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to delete skill');
  }
  // 204 No Content returns no body
  return { success: true };
}

async function fetchSkillContent(skillClass, skillName, fileName = null) {
  const url = fileName
    ? `${API_BASE}/skills/content/${skillClass}/${skillName}?file=${encodeURIComponent(fileName)}`
    : `${API_BASE}/skills/content/${skillClass}/${skillName}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch skill content');
  return res.json();
}

async function fetchAgentSkillContent(agentId, skillName, fileName = null) {
  const url = fileName
    ? `${API_BASE}/skills/content/agent/${agentId}/${skillName}?file=${encodeURIComponent(fileName)}`
    : `${API_BASE}/skills/content/agent/${agentId}/${skillName}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch agent skill content');
  return res.json();
}

// Category API functions
async function fetchCategories() {
  const res = await fetch(`${API_BASE}/skills/categories`, {
    credentials: 'include'
  });
  if (!res.ok) throw new Error('Failed to fetch categories');
  return res.json();
}

async function createCategory(data) {
  const res = await fetch(`${API_BASE}/skills/categories`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to create category');
  }
  return res.json();
}

async function fetchDbSkills() {
  const res = await fetch(`${API_BASE}/skills/db-skills`, {
    credentials: 'include'
  });
  if (!res.ok) throw new Error('Failed to fetch skills');
  return res.json();
}

async function assignSkillCategory(skillId, categoryId) {
  const res = await fetch(`${API_BASE}/skills/db-skills/${skillId}/category`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ categoryId })
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to assign category');
  }
  return res.json();
}

async function syncSkillsFromFilesystem() {
  const res = await fetch(`${API_BASE}/skills/db-skills/sync`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Sync failed');
  }
  return res.json();
}

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

// Map file extensions to language names for syntax highlighting
function getLanguageFromFileName(fileName) {
  if (!fileName) return 'text';
  const ext = fileName.split('.').pop()?.toLowerCase();
  const langMap = {
    js: 'javascript',
    jsx: 'jsx',
    ts: 'typescript',
    tsx: 'tsx',
    py: 'python',
    rb: 'ruby',
    sh: 'bash',
    bash: 'bash',
    zsh: 'bash',
    yml: 'yaml',
    yaml: 'yaml',
    json: 'json',
    md: 'markdown',
    css: 'css',
    scss: 'scss',
    html: 'html',
    xml: 'xml',
    sql: 'sql',
    go: 'go',
    rs: 'rust',
    java: 'java',
    kt: 'kotlin',
    swift: 'swift',
    c: 'c',
    cpp: 'cpp',
    h: 'c',
    hpp: 'cpp',
    php: 'php',
    lua: 'lua',
    toml: 'toml',
    ini: 'ini',
    dockerfile: 'dockerfile',
    makefile: 'makefile'
  };
  return langMap[ext] || 'text';
}

// ============================================
// Sub-Components
// ============================================

// Copy button for code blocks
const CopyButton = memo(function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-all
        ${copied ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 hover:bg-gray-600 text-gray-400 hover:text-gray-200'}`}
      title={copied ? 'Copied!' : 'Copy to clipboard'}
    >
      {copied ? <><Check className="w-3 h-3" /><span>Copied</span></> : <><Copy className="w-3 h-3" /><span>Copy</span></>}
    </button>
  );
});

// Code block with syntax highlighting
const CodeBlock = memo(function CodeBlock({ language, children }) {
  const codeString = String(children).replace(/\n$/, '');

  return (
    <div className="relative group my-2">
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <CopyButton text={codeString} />
      </div>
      <Suspense fallback={
        <pre className="bg-gray-900 rounded-lg p-3 text-xs text-gray-300 font-mono overflow-x-auto">
          <code>{children}</code>
        </pre>
      }>
        <SyntaxHighlighter
          language={language}
          style={oneDark}
          customStyle={{
            margin: 0,
            borderRadius: '0.5rem',
            fontSize: '0.75rem',
            lineHeight: '1.5'
          }}
          showLineNumbers={codeString.split('\n').length > 3}
          wrapLines={true}
          wrapLongLines={true}
        >
          {codeString}
        </SyntaxHighlighter>
      </Suspense>
    </div>
  );
});

const SkillClassBadge = memo(function SkillClassBadge({ skillClass }) {
  const colors = SKILL_CLASS_COLORS[skillClass] || SKILL_CLASS_COLORS.custom;

  return (
    <span
      className={`
        inline-flex items-center px-2 py-0.5 text-xs font-medium rounded
        ${colors.bg} ${colors.text} ${colors.border} border
      `}
    >
      {colors.label}
    </span>
  );
});

const InstallStatusBadge = memo(function InstallStatusBadge({ installed }) {
  if (installed === true) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-green-500/20 text-green-400 border border-green-500/30">
        <Check className="w-3 h-3" />
        Installed
      </span>
    );
  }
  if (installed === false) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-red-500/20 text-red-400 border border-red-500/30">
        <X className="w-3 h-3" />
        Missing
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-gray-500/20 text-gray-400 border border-gray-500/30">
      Not verified
    </span>
  );
});

const AgentSelector = memo(function AgentSelector({
  agents,
  selectedAgent,
  onSelect,
  isLoading
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className={`
          flex items-center justify-between w-64 px-4 py-2.5
          bg-gray-800 border border-gray-700 rounded-lg
          text-left text-gray-200
          hover:border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors
        `}
      >
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4 text-gray-400" />
          <span className="truncate">
            {selectedAgent?.displayName || selectedAgent?.name || 'Select Agent'}
          </span>
        </div>
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        ) : (
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden">
          <div className="max-h-60 overflow-y-auto">
            {agents.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500">
                No active agents found
              </div>
            ) : (
              agents.map((agent) => (
                <button
                  key={agent.id}
                  type="button"
                  onClick={() => {
                    onSelect(agent);
                    setIsOpen(false);
                  }}
                  className={`
                    w-full px-4 py-2.5 text-left text-sm
                    ${selectedAgent?.id === agent.id
                      ? 'bg-indigo-600/30 text-white'
                      : 'text-gray-300 hover:bg-gray-700'
                    }
                    transition-colors
                  `}
                >
                  <div className="flex items-center gap-2">
                    <Bot className="w-4 h-4 text-gray-400" />
                    <span>{agent.displayName || agent.name}</span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
});

const SkillTemplateItem = memo(function SkillTemplateItem({
  skill,
  isSelected,
  onToggle,
  onPush,
  onView,
  isProcessing
}) {
  return (
    <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg border border-gray-700/50 hover:border-gray-600 transition-colors">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => onToggle(skill.name)}
          className="text-gray-400 hover:text-indigo-400 transition-colors"
        >
          {isSelected ? (
            <CheckSquare className="w-5 h-5 text-indigo-400" />
          ) : (
            <Square className="w-5 h-5" />
          )}
        </button>
        <Package className="w-5 h-5 text-gray-400" />
        <div>
          <span className="font-medium text-gray-200">{skill.name}</span>
          <div className="text-xs text-gray-500 mt-0.5">{skill.path}</div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <SkillClassBadge skillClass={skill.class} />
        {onView && (
          <button
            type="button"
            onClick={() => onView(skill)}
            disabled={isProcessing}
            className="p-1.5 text-gray-400 hover:text-indigo-400 hover:bg-indigo-500/10 rounded transition-colors disabled:opacity-50"
            title="View skill content"
          >
            <Eye className="w-4 h-4" />
          </button>
        )}
        {onPush && (
          <button
            type="button"
            onClick={() => onPush(skill.class, skill.name)}
            disabled={isProcessing}
            className="p-1.5 text-gray-400 hover:text-green-400 hover:bg-green-500/10 rounded transition-colors disabled:opacity-50"
            title="Push this skill"
          >
            <Upload className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
});

const AgentSkillItem = memo(function AgentSkillItem({
  skill,
  isSelected,
  onToggle,
  onPush,
  onImport,
  onDelete,
  onView,
  isProcessing
}) {
  // Skills with class "unknown" only exist on agent, not in backend templates
  const isAgentOnly = skill.class === 'unknown';
  // Skills in backend templates can be pushed
  const canPush = !isAgentOnly && !skill.isReserved && onPush;
  // Agent-only skills can be imported to backend
  const canImport = isAgentOnly && !skill.isReserved && onImport;
  // All non-reserved skills that are installed can be deleted
  const canDelete = !skill.isReserved && skill.installed && onDelete;

  return (
    <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg border border-gray-700/50 hover:border-gray-600 transition-colors">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => onToggle(skill.name)}
          className="text-gray-400 hover:text-indigo-400 transition-colors"
        >
          {isSelected ? (
            <CheckSquare className="w-5 h-5 text-indigo-400" />
          ) : (
            <Square className="w-5 h-5" />
          )}
        </button>
        {skill.isReserved ? (
          <Shield className="w-5 h-5 text-purple-400" />
        ) : (
          <Package className="w-5 h-5 text-gray-400" />
        )}
        <div>
          <span className="font-medium text-gray-200">{skill.name}</span>
          {skill.isReserved && (
            <span className="ml-2 text-xs text-purple-400">(System)</span>
          )}
          {isAgentOnly && (
            <span className="ml-2 text-xs text-yellow-400">(Agent only)</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <SkillClassBadge skillClass={skill.class || 'builtin'} />
        <InstallStatusBadge installed={skill.installed} />
        {onView && (
          <button
            type="button"
            onClick={() => onView(skill)}
            disabled={isProcessing}
            className="p-1.5 text-gray-400 hover:text-indigo-400 hover:bg-indigo-500/10 rounded transition-colors disabled:opacity-50"
            title="View skill content"
          >
            <Eye className="w-4 h-4" />
          </button>
        )}
        {canPush && (
          <button
            type="button"
            onClick={() => onPush(skill.class, skill.name)}
            disabled={isProcessing}
            className="p-1.5 text-gray-400 hover:text-green-400 hover:bg-green-500/10 rounded transition-colors disabled:opacity-50"
            title="Push this skill to agent"
          >
            <Upload className="w-4 h-4" />
          </button>
        )}
        {canImport && (
          <button
            type="button"
            onClick={() => onImport(skill.name)}
            disabled={isProcessing}
            className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-colors disabled:opacity-50"
            title="Import this skill to backend templates"
          >
            <Download className="w-4 h-4" />
          </button>
        )}
        {canDelete && (
          <button
            type="button"
            onClick={() => onDelete(skill.name)}
            disabled={isProcessing}
            className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
            title="Delete skill from agent"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
});

const StatusMessage = memo(function StatusMessage({ type, message, onDismiss }) {
  const isError = type === 'error';

  return (
    <div
      className={`
        flex items-center justify-between p-3 rounded-lg
        ${isError ? 'bg-red-500/10 border border-red-500/30' : 'bg-green-500/10 border border-green-500/30'}
      `}
    >
      <div className="flex items-center gap-2">
        {isError ? (
          <AlertCircle className="w-5 h-5 text-red-400" />
        ) : (
          <CheckCircle className="w-5 h-5 text-green-400" />
        )}
        <span className={isError ? 'text-red-300' : 'text-green-300'}>
          {message}
        </span>
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="p-1 hover:bg-gray-700 rounded transition-colors"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      )}
    </div>
  );
});

const SkillDetailPanel = memo(function SkillDetailPanel({
  skillData,
  isLoading,
  onClose,
  onFileSelect,
  selectedFile
}) {
  if (!skillData && !isLoading) return null;

  return (
    <div className="w-96 flex-shrink-0 border-l border-gray-700 bg-gray-850 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700">
        <div className="flex items-center gap-2 min-w-0">
          <Package className="w-5 h-5 text-indigo-400 flex-shrink-0" />
          <span className="font-medium text-white truncate">
            {skillData?.name || 'Loading...'}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
        </div>
      ) : skillData ? (
        <>
          {/* Metadata */}
          <div className="p-4 border-b border-gray-700 space-y-2">
            <div className="flex items-center gap-2">
              <SkillClassBadge skillClass={skillData.class} />
              <span className="text-xs text-gray-500">
                {formatBytes(skillData.totalSize)} total
              </span>
            </div>
            {skillData.agentName && (
              <div className="text-xs text-gray-400">
                From: <span className="text-gray-300">{skillData.agentName}</span>
              </div>
            )}
          </div>

          {/* File List */}
          <div className="p-3 border-b border-gray-700">
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
              Files ({skillData.files.length})
            </div>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {skillData.files.map((file) => (
                <button
                  key={file.name}
                  type="button"
                  onClick={() => onFileSelect(file.name)}
                  className={`
                    w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm
                    ${selectedFile === file.name || (selectedFile === null && file.name === 'SKILL.md')
                      ? 'bg-indigo-600/30 text-white'
                      : 'text-gray-300 hover:bg-gray-700'
                    }
                    transition-colors
                  `}
                >
                  {file.name.endsWith('.md') ? (
                    <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  ) : (
                    <File className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  )}
                  <span className="truncate flex-1">{file.name}</span>
                  <span className="text-xs text-gray-500 flex-shrink-0">
                    {formatBytes(file.size)}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Content Preview */}
          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            <div className="px-4 py-2 text-xs text-gray-400 flex items-center gap-2 flex-shrink-0">
              <FileText className="w-3 h-3" />
              {skillData.contentFile}
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              {skillData.contentFile?.endsWith('.md') ? (
                <div className="text-sm text-gray-300 prose prose-sm prose-invert max-w-none prose-headings:text-gray-200 prose-headings:font-semibold prose-h1:text-lg prose-h2:text-base prose-h3:text-sm prose-p:my-2 prose-pre:my-0 prose-code:text-orange-300 prose-code:bg-transparent prose-code:px-0 prose-code:py-0 prose-code:rounded-none prose-code:before:content-none prose-code:after:content-none prose-ul:my-2 prose-li:my-0.5 prose-a:text-indigo-400 bg-gray-900/50 p-4 rounded border border-gray-700">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({ node, inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || '');
                        const lang = match ? match[1] : 'text';

                        if (!inline) {
                          return <CodeBlock language={lang}>{children}</CodeBlock>;
                        }

                        return (
                          <code className="text-orange-300 bg-gray-800 px-1 py-0.5 rounded text-xs" {...props}>
                            {children}
                          </code>
                        );
                      }
                    }}
                  >
                    {skillData.content || '(empty file)'}
                  </ReactMarkdown>
                </div>
              ) : (
                <div className="relative group">
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <CopyButton text={skillData.content || ''} />
                  </div>
                  <CodeBlock language={getLanguageFromFileName(skillData.contentFile)}>
                    {skillData.content || '(empty file)'}
                  </CodeBlock>
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
});

// ============================================
// Main Component
// ============================================

export default function SkillManager({ className = '' }) {
  // State
  const [currentView, setCurrentView] = useState(VIEWS.AGENT_SKILLS);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentSkills, setAgentSkills] = useState([]);
  const [templates, setTemplates] = useState({ builtin: [], skills: [], imported: [] });
  const [categories, setCategories] = useState([]);
  const [dbSkills, setDbSkills] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSkills, setSelectedSkills] = useState(new Set());

  const [isLoadingAgents, setIsLoadingAgents] = useState(true);
  const [isLoadingSkills, setIsLoadingSkills] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const [statusMessage, setStatusMessage] = useState(null);
  const [agentRoles, setAgentRoles] = useState([]);

  // Skill detail panel state
  const [detailSkill, setDetailSkill] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [selectedDetailFile, setSelectedDetailFile] = useState(null);

  // Category creation state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDisplayName, setNewCategoryDisplayName] = useState('');
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);

  // Effects
  useEffect(() => {
    loadAgents();
    loadTemplates();
  }, []);

  useEffect(() => {
    if (selectedAgent) {
      loadAgentSkills(false);
      setSelectedSkills(new Set());
      // Reset status message when switching agents
      setStatusMessage(null);
    }
  }, [selectedAgent?.id]);

  // API Handlers
  const loadAgents = useCallback(async () => {
    setIsLoadingAgents(true);
    try {
      const result = await fetchAgents();
      setAgents(result.data || []);
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsLoadingAgents(false);
    }
  }, []);

  const loadAgentSkills = useCallback(async (verify = false) => {
    if (!selectedAgent) return;
    if (verify) {
      setIsVerifying(true);
    } else {
      setIsLoadingSkills(true);
    }
    try {
      const result = await fetchAgentSkills(selectedAgent.id, verify);
      setAgentSkills(result.data || []);
      setAgentRoles(result.roles || []);
      if (verify && result.verification) {
        const { installed, missing, errors } = result.verification;
        if (errors && errors.length > 0) {
          setStatusMessage({ type: 'error', message: `Verify failed: ${errors[0]}` });
        } else {
          setStatusMessage({
            type: 'success',
            message: `Verified: ${installed.length} installed, ${missing.length} missing`
          });
        }
      }
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsLoadingSkills(false);
      setIsVerifying(false);
    }
  }, [selectedAgent]);

  const loadTemplates = useCallback(async () => {
    try {
      // Sync filesystem to DB first to ensure all skills are registered
      await syncSkillsFromFilesystem().catch(() => {});
      const [templatesResult, categoriesResult, dbSkillsResult] = await Promise.all([
        fetchSkillTemplates(),
        fetchCategories(),
        fetchDbSkills()
      ]);
      setTemplates(templatesResult.data || { builtin: [], skills: [], imported: [] });
      setCategories(categoriesResult.data || []);
      setDbSkills(dbSkillsResult.data || []);
    } catch (err) {
      console.error('Failed to load skill templates:', err);
    }
  }, []);

  const handlePushSingleSkill = useCallback(async (skillClass, skillName) => {
    if (!selectedAgent) return;
    setIsProcessing(true);
    try {
      await pushSkillsToAgent(selectedAgent.id, skillClass, skillName);
      setStatusMessage({ type: 'success', message: `Pushed skill: ${skillName}` });
      await loadAgentSkills(true); // Verify after push
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [selectedAgent, loadAgentSkills]);

  const handlePushAllSkills = useCallback(async () => {
    if (!selectedAgent) return;
    setIsProcessing(true);
    try {
      const result = await pushSkillsToAgent(selectedAgent.id);
      const data = result.data;
      const builtinCount = data.builtin?.pushed?.length || 0;
      const rolesCount = data.roles?.pushed?.length || 0;
      setStatusMessage({
        type: 'success',
        message: `Pushed ${builtinCount} builtin + ${rolesCount} role skills`
      });
      await loadAgentSkills(true); // Verify after push
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [selectedAgent, loadAgentSkills]);

  const handlePushSelected = useCallback(async () => {
    if (!selectedAgent || selectedSkills.size === 0) return;
    setIsProcessing(true);
    let successCount = 0;
    let failCount = 0;

    for (const skillName of selectedSkills) {
      // Find the skill to get its class
      const allSkills = [...templates.builtin, ...templates.roles, ...templates.custom];
      const skill = allSkills.find(s => s.name === skillName);
      if (skill) {
        try {
          await pushSkillsToAgent(selectedAgent.id, skill.class, skill.name);
          successCount++;
        } catch {
          failCount++;
        }
      }
    }

    setStatusMessage({
      type: failCount > 0 ? 'error' : 'success',
      message: `Pushed ${successCount} skills${failCount > 0 ? `, ${failCount} failed` : ''}`
    });
    setSelectedSkills(new Set());
    await loadAgentSkills(true);
    setIsProcessing(false);
  }, [selectedAgent, selectedSkills, templates, loadAgentSkills]);

  const handleAggregate = useCallback(async () => {
    if (!selectedAgent) return;
    setIsProcessing(true);
    try {
      const result = await aggregateSkills(selectedAgent.id);
      const data = result.data;
      setStatusMessage({
        type: data.errors?.length > 0 ? 'error' : 'success',
        message: `Aggregation complete: +${data.addedSkills?.length || 0} -${data.removedSkills?.length || 0} skills`
      });
      await loadAgentSkills(false);
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [selectedAgent, loadAgentSkills]);

  const handleValidate = useCallback(async () => {
    if (!selectedAgent) return;
    setIsProcessing(true);
    try {
      const result = await validateSkills(selectedAgent.id);
      const data = result.data;
      setStatusMessage({
        type: data.valid ? 'success' : 'error',
        message: data.valid
          ? 'All skills valid'
          : `Missing: ${data.missingSkills?.join(', ') || 'unknown'}`
      });
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [selectedAgent]);

  const handleRegenerateIdentity = useCallback(async () => {
    if (!selectedAgent) return;
    setIsProcessing(true);
    try {
      await regenerateIdentity(selectedAgent.id);
      setStatusMessage({ type: 'success', message: 'Identity skill regenerated' });
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [selectedAgent]);

  const handleImportSkill = useCallback(async (skillName) => {
    if (!selectedAgent) return;
    setIsProcessing(true);
    try {
      await importSkillFromAgent(selectedAgent.id, skillName);
      setStatusMessage({ type: 'success', message: `Imported skill: ${skillName} to custom templates` });
      // Sync filesystem to DB so the imported skill appears in dbSkills
      await syncSkillsFromFilesystem();
      // Reload templates to show the newly imported skill
      await loadTemplates();
      // Reload agent skills to update the view
      await loadAgentSkills(true);
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [selectedAgent, loadTemplates, loadAgentSkills]);

  const handleDeleteSkill = useCallback(async (skillName) => {
    if (!selectedAgent) return;
    // Confirm deletion
    if (!window.confirm(`Delete skill "${skillName}" from agent "${selectedAgent.displayName || selectedAgent.name}"?`)) {
      return;
    }
    setIsProcessing(true);
    try {
      await deleteSkillFromAgent(selectedAgent.id, skillName);
      setStatusMessage({ type: 'success', message: `Deleted skill: ${skillName}` });
      // Reload agent skills to update the view
      await loadAgentSkills(true);
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [selectedAgent, loadAgentSkills]);

  const toggleSkillSelection = useCallback((skillName) => {
    setSelectedSkills(prev => {
      const next = new Set(prev);
      if (next.has(skillName)) {
        next.delete(skillName);
      } else {
        next.add(skillName);
      }
      return next;
    });
  }, []);

  // Skill detail handlers
  const handleViewSkill = useCallback(async (skill) => {
    setDetailSkill(skill);
    setDetailData(null);
    setSelectedDetailFile(null);
    setIsLoadingDetail(true);

    try {
      let result;
      if (skill.class === 'unknown' && selectedAgent) {
        // Agent-only skill - fetch via SSH
        result = await fetchAgentSkillContent(selectedAgent.id, skill.name);
      } else {
        // Backend template skill
        result = await fetchSkillContent(skill.class, skill.name);
      }
      setDetailData(result.data);
    } catch (err) {
      setStatusMessage({ type: 'error', message: `Failed to load skill: ${err.message}` });
      setDetailSkill(null);
    } finally {
      setIsLoadingDetail(false);
    }
  }, [selectedAgent]);

  const handleDetailFileSelect = useCallback(async (fileName) => {
    if (!detailSkill) return;
    setSelectedDetailFile(fileName);
    setIsLoadingDetail(true);

    try {
      let result;
      if (detailSkill.class === 'unknown' && selectedAgent) {
        result = await fetchAgentSkillContent(selectedAgent.id, detailSkill.name, fileName);
      } else {
        result = await fetchSkillContent(detailSkill.class, detailSkill.name, fileName);
      }
      setDetailData(result.data);
    } catch (err) {
      setStatusMessage({ type: 'error', message: `Failed to load file: ${err.message}` });
    } finally {
      setIsLoadingDetail(false);
    }
  }, [detailSkill, selectedAgent]);

  const handleCloseDetail = useCallback(() => {
    setDetailSkill(null);
    setDetailData(null);
    setSelectedDetailFile(null);
  }, []);

  // Category creation handler
  const handleCreateCategory = useCallback(async () => {
    if (!newCategoryName.trim() || !newCategoryDisplayName.trim()) {
      setStatusMessage({ type: 'error', message: 'Name and display name are required' });
      return;
    }

    setIsCreatingCategory(true);
    try {
      await createCategory({
        name: newCategoryName.trim().toLowerCase().replace(/\s+/g, '-'),
        displayName: newCategoryDisplayName.trim()
      });
      setStatusMessage({ type: 'success', message: `Category "${newCategoryDisplayName}" created` });
      setShowCategoryModal(false);
      setNewCategoryName('');
      setNewCategoryDisplayName('');
      // Reload templates to show new category
      await loadTemplates();
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsCreatingCategory(false);
    }
  }, [newCategoryName, newCategoryDisplayName, loadTemplates]);

  // Drag & Drop handler for moving skills between categories
  const handleSkillDrop = useCallback(async (skillId, targetCategoryId) => {
    try {
      await assignSkillCategory(skillId, targetCategoryId);
      setStatusMessage({ type: 'success', message: 'Skill moved successfully' });
      await loadTemplates();
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    }
  }, [loadTemplates]);

  // Computed
  const filteredAgentSkills = useMemo(() => {
    if (!searchQuery) return agentSkills;
    const query = searchQuery.toLowerCase();
    return agentSkills.filter(s => s.name.toLowerCase().includes(query));
  }, [agentSkills, searchQuery]);

  // Split skills into Expected (from backend) and Discovered (agent-only)
  const { expectedSkills, discoveredSkills } = useMemo(() => {
    const expected = filteredAgentSkills.filter(s => s.class !== 'unknown');
    const discovered = filteredAgentSkills.filter(s => s.class === 'unknown');
    return { expectedSkills: expected, discoveredSkills: discovered };
  }, [filteredAgentSkills]);

  const allTemplates = useMemo(() => {
    return [
      ...(templates.builtin || []),
      ...(templates.skills || []),
      ...(templates.imported || [])
    ];
  }, [templates]);

  // Render
  return (
    <div className={`flex flex-col h-full bg-gray-900 ${className}`}>
      {/* Header */}
      <header className="flex-shrink-0 border-b border-gray-800">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6 text-indigo-400" />
            <h1 className="text-xl font-semibold text-white">Skill Manager</h1>
          </div>

          {/* View Tabs */}
          <div className="flex items-center gap-2 bg-gray-800 rounded-lg p-1">
            <button
              type="button"
              onClick={() => setCurrentView(VIEWS.AGENT_SKILLS)}
              className={`
                flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors
                ${currentView === VIEWS.AGENT_SKILLS
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }
              `}
            >
              <Bot className="w-4 h-4" />
              Agent Skills
            </button>
            <button
              type="button"
              onClick={() => setCurrentView(VIEWS.TEMPLATES)}
              className={`
                flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors
                ${currentView === VIEWS.TEMPLATES
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }
              `}
            >
              <Database className="w-4 h-4" />
              My Templates
              {allTemplates.length > 0 && (
                <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-medium rounded-full bg-indigo-500/20 text-indigo-300">
                  {allTemplates.length}
                </span>
              )}
            </button>
          </div>

          <div className="w-6" />
        </div>

        {/* Agent Selector + Actions */}
        {currentView === VIEWS.AGENT_SKILLS && (
          <div className="flex items-center justify-between px-6 py-3 bg-gray-800/50">
            <div className="flex items-center gap-4">
              <AgentSelector
                agents={agents}
                selectedAgent={selectedAgent}
                onSelect={setSelectedAgent}
                isLoading={isLoadingAgents}
              />
              {agentRoles.length > 0 && (
                <div className="text-sm text-gray-400">
                  Roles: <span className="text-gray-300">{agentRoles.join(', ')}</span>
                </div>
              )}
            </div>

            {selectedAgent && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => loadAgentSkills(true)}
                  disabled={isProcessing || isVerifying}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-200 bg-gray-700 hover:bg-gray-600 rounded-lg disabled:opacity-50 transition-colors"
                  title="Verify installed status via SSH"
                >
                  {isVerifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                  Verify
                </button>
                <button
                  type="button"
                  onClick={handlePushAllSkills}
                  disabled={isProcessing}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-500 rounded-lg disabled:opacity-50 transition-colors"
                  title="Push all skills (builtin + roles) to agent via SCP"
                >
                  {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Push All
                </button>
                <button
                  type="button"
                  onClick={handleRegenerateIdentity}
                  disabled={isProcessing}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-200 bg-gray-700 hover:bg-gray-600 rounded-lg disabled:opacity-50 transition-colors"
                >
                  {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Identity
                </button>
              </div>
            )}
          </div>
        )}

        {/* Template Actions */}
        {currentView === VIEWS.TEMPLATES && selectedAgent && (
          <div className="flex items-center justify-between px-6 py-3 bg-gray-800/50">
            <div className="flex items-center gap-4">
              <AgentSelector
                agents={agents}
                selectedAgent={selectedAgent}
                onSelect={setSelectedAgent}
                isLoading={isLoadingAgents}
              />
              <span className="text-sm text-gray-400">
                Select templates to push to agent
              </span>
            </div>
            {selectedSkills.size > 0 && (
              <button
                type="button"
                onClick={handlePushSelected}
                disabled={isProcessing}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-500 rounded-lg disabled:opacity-50 transition-colors"
              >
                {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Push Selected ({selectedSkills.size})
              </button>
            )}
          </div>
        )}

        {currentView === VIEWS.TEMPLATES && !selectedAgent && (
          <div className="px-6 py-3 bg-gray-800/50 text-sm text-gray-400">
            Select an agent above to enable pushing skills
          </div>
        )}
      </header>

      {/* Status Messages */}
      {statusMessage && (
        <div className="px-6 pt-4">
          <StatusMessage
            type={statusMessage.type}
            message={statusMessage.message}
            onDismiss={() => setStatusMessage(null)}
          />
        </div>
      )}

      {/* Content with optional Side Panel */}
      <div className="flex-1 overflow-hidden flex">
        {/* Main Content */}
        <div className="flex-1 overflow-hidden flex flex-col min-w-0">
          {/* Search Bar */}
          <div className="flex items-center gap-4 px-6 py-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={currentView === VIEWS.AGENT_SKILLS ? "Search agent skills..." : "Search templates..."}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Skills List */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {currentView === VIEWS.AGENT_SKILLS ? (
            selectedAgent ? (
              isLoadingSkills ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
                </div>
              ) : filteredAgentSkills.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No skills found for this agent</p>
                  <p className="text-sm mt-2">Click "Push All" to install skill templates</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Expected Skills - from backend templates */}
                  {expectedSkills.length > 0 && (
                    <div>
                      <h2 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">
                        Expected Skills ({expectedSkills.length})
                      </h2>
                      <div className="space-y-2">
                        {expectedSkills.map((skill) => (
                          <AgentSkillItem
                            key={skill.name}
                            skill={skill}
                            isSelected={selectedSkills.has(skill.name)}
                            onToggle={toggleSkillSelection}
                            onPush={handlePushSingleSkill}
                            onImport={handleImportSkill}
                            onDelete={handleDeleteSkill}
                            onView={handleViewSkill}
                            isProcessing={isProcessing}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Discovered Skills - only exist on agent */}
                  {discoveredSkills.length > 0 && (
                    <div>
                      <h2 className="text-sm font-medium text-yellow-400 uppercase tracking-wider mb-3">
                        Discovered Skills ({discoveredSkills.length})
                      </h2>
                      <p className="text-xs text-gray-500 mb-3">
                        Skills found on agent that are not in backend templates. Use Import to add them.
                      </p>
                      <div className="space-y-2">
                        {discoveredSkills.map((skill) => (
                          <AgentSkillItem
                            key={skill.name}
                            skill={skill}
                            isSelected={selectedSkills.has(skill.name)}
                            onToggle={toggleSkillSelection}
                            onPush={handlePushSingleSkill}
                            onImport={handleImportSkill}
                            onDelete={handleDeleteSkill}
                            onView={handleViewSkill}
                            isProcessing={isProcessing}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <Bot className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                  <h2 className="text-xl font-medium text-gray-400 mb-2">
                    Select an Agent
                  </h2>
                  <p className="text-gray-500 max-w-sm">
                    Choose an active agent from the dropdown above to view and manage their skills.
                  </p>
                </div>
              </div>
            )
          ) : (
            // Templates View - grouped by categories from DB
            <div className="space-y-6">
              {/* Builtin Skills (fixed section) */}
              {templates.builtin?.length > 0 && (
                <div>
                  <h2 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">
                    Builtin Skills ({templates.builtin.length})
                  </h2>
                  <div className="space-y-2">
                    {templates.builtin
                      .filter(s => !searchQuery || s.name.toLowerCase().includes(searchQuery.toLowerCase()))
                      .map((skill) => (
                        <SkillTemplateItem
                          key={skill.name}
                          skill={skill}
                          isSelected={selectedSkills.has(skill.name)}
                          onToggle={toggleSkillSelection}
                          onPush={selectedAgent ? handlePushSingleSkill : null}
                          onView={handleViewSkill}
                          isProcessing={isProcessing}
                        />
                      ))
                    }
                  </div>
                </div>
              )}

              {/* Skills Section Header with New Category button */}
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium text-gray-400 uppercase tracking-wider">
                  Skills by Category
                </h2>
                <button
                  type="button"
                  onClick={() => setShowCategoryModal(true)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10 rounded transition-colors"
                  title="Create new category"
                >
                  <FolderPlus className="w-4 h-4" />
                  New Category
                </button>
              </div>

              {/* Categories with their skills */}
              {categories.map((category) => {
                const categorySkills = dbSkills.filter(s =>
                  s.categoryId === category.id &&
                  (s.class === 'skills' || s.class === 'imported') &&
                  (!searchQuery || s.name.toLowerCase().includes(searchQuery.toLowerCase()))
                );

                return (
                  <div
                    key={category.id}
                    className="border border-gray-700 rounded-lg p-3 bg-gray-800/50"
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add('border-indigo-500', 'bg-indigo-500/10');
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                      const skillId = e.dataTransfer.getData('skillId');
                      if (skillId) {
                        handleSkillDrop(skillId, category.id);
                      }
                    }}
                  >
                    <h3 className="text-sm font-medium text-white mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      {category.displayName}
                      <span className="text-xs text-gray-500">({categorySkills.length})</span>
                    </h3>
                    {categorySkills.length > 0 ? (
                      <div className="space-y-2">
                        {categorySkills.map((skill) => {
                          const templateSkill = skill.class === 'imported'
                            ? templates.imported?.find(t => t.name === skill.name)
                            : templates.skills?.find(t => t.name === skill.name);
                          return (
                            <div
                              key={skill.id}
                              draggable
                              onDragStart={(e) => {
                                e.dataTransfer.setData('skillId', skill.id);
                                e.currentTarget.classList.add('opacity-50');
                              }}
                              onDragEnd={(e) => {
                                e.currentTarget.classList.remove('opacity-50');
                              }}
                              className="cursor-move"
                            >
                              <SkillTemplateItem
                                skill={templateSkill || { name: skill.name, class: skill.class, path: '' }}
                                isSelected={selectedSkills.has(skill.name)}
                                onToggle={toggleSkillSelection}
                                onPush={selectedAgent ? handlePushSingleSkill : null}
                                onView={handleViewSkill}
                                isProcessing={isProcessing}
                              />
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 italic">Drop skills here</p>
                    )}
                  </div>
                );
              })}

              {/* Uncategorized Skills */}
              {(() => {
                const uncategorizedSkills = dbSkills.filter(s =>
                  !s.categoryId &&
                  (s.class === 'skills' || s.class === 'imported') &&
                  (!searchQuery || s.name.toLowerCase().includes(searchQuery.toLowerCase()))
                );

                if (uncategorizedSkills.length === 0) return null;

                return (
                  <div
                    className="border border-gray-700 border-dashed rounded-lg p-3 bg-gray-800/30"
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add('border-yellow-500', 'bg-yellow-500/10');
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove('border-yellow-500', 'bg-yellow-500/10');
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-yellow-500', 'bg-yellow-500/10');
                      const skillId = e.dataTransfer.getData('skillId');
                      if (skillId) {
                        handleSkillDrop(skillId, null);
                      }
                    }}
                  >
                    <h3 className="text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                      Uncategorized
                      <span className="text-xs text-gray-500">({uncategorizedSkills.length})</span>
                    </h3>
                    <div className="space-y-2">
                      {uncategorizedSkills.map((skill) => {
                        const templateSkill = skill.class === 'imported'
                          ? templates.imported?.find(t => t.name === skill.name)
                          : templates.skills?.find(t => t.name === skill.name);
                        return (
                          <div
                            key={skill.id}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('skillId', skill.id);
                              e.currentTarget.classList.add('opacity-50');
                            }}
                            onDragEnd={(e) => {
                              e.currentTarget.classList.remove('opacity-50');
                            }}
                            className="cursor-move"
                          >
                            <SkillTemplateItem
                              skill={templateSkill || { name: skill.name, class: skill.class, path: '' }}
                              isSelected={selectedSkills.has(skill.name)}
                              onToggle={toggleSkillSelection}
                              onPush={selectedAgent ? handlePushSingleSkill : null}
                              onView={handleViewSkill}
                              isProcessing={isProcessing}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {allTemplates.length === 0 && categories.length === 0 && dbSkills.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No skill templates found</p>
                </div>
              )}
            </div>
          )}
        </div>
        </div>

        {/* Side Panel for Skill Details */}
        {detailSkill && (
          <SkillDetailPanel
            skillData={detailData}
            isLoading={isLoadingDetail}
            onClose={handleCloseDetail}
            onFileSelect={handleDetailFileSelect}
            selectedFile={selectedDetailFile}
          />
        )}
      </div>

      {/* Category Creation Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-gray-800 rounded-xl border border-gray-700 shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <FolderPlus className="w-5 h-5 text-indigo-400" />
                Create Category
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowCategoryModal(false);
                  setNewCategoryName('');
                  setNewCategoryDisplayName('');
                }}
                className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Display Name
                </label>
                <input
                  type="text"
                  value={newCategoryDisplayName}
                  onChange={(e) => {
                    setNewCategoryDisplayName(e.target.value);
                    // Auto-generate slug from display name
                    const slug = e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                    setNewCategoryName(slug);
                  }}
                  placeholder="e.g., Media Server"
                  className="w-full px-4 py-2.5 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Slug (folder name)
                </label>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))}
                  placeholder="e.g., media-server"
                  className="w-full px-4 py-2.5 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  Lowercase letters, numbers, and hyphens only
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-700 bg-gray-850">
              <button
                type="button"
                onClick={() => {
                  setShowCategoryModal(false);
                  setNewCategoryName('');
                  setNewCategoryDisplayName('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateCategory}
                disabled={isCreatingCategory || !newCategoryName.trim() || !newCategoryDisplayName.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isCreatingCategory ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Create
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Named Exports
export {
  SkillClassBadge,
  InstallStatusBadge,
  AgentSelector,
  SkillTemplateItem,
  AgentSkillItem,
  StatusMessage,
  SKILL_CLASS_COLORS,
  VIEWS
};
